import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetSuper4 } from "~hooks/useSincronizacao";
import { super4Mock } from "../mocks/new/Super4.mock";
import useVendedor from "~fetchs/useVendedor";
import { formatMes } from "../utils/formatters";
import { usarMock } from "../utils/dataSource";


const hasMock = usarMock(super4Mock);


const mergeByAnoMes = (items, sumKeys, avgKeys) => {
  const map = new Map();

  for (const item of items) {
    const key = item.ytd ? '__ytd__' : (item.anoMes ?? '__');
    if (!map.has(key)) map.set(key, { ...item, ...Object.fromEntries(sumKeys.map(field => [field, 0])), _n: 0 });
    const entry = map.get(key);
    entry._n++;
    for (const field of sumKeys) entry[field] = (entry[field] ?? 0) + (item[field] ?? 0);
    for (const field of avgKeys) entry[`_s_${field}`] = (entry[`_s_${field}`] ?? 0) + (item[field] ?? 0);
  }

  return Array.from(map.values()).map(entry => {
    const result = { ...entry };
    delete result._n;
    for (const field of avgKeys) {
      result[field] = entry._n ? entry[`_s_${field}`] / entry._n : 0;
      delete result[`_s_${field}`];
    }
    return result;
  });
};

const flattenVendedorSuper4 = (rawArr, vendedorSelecionado) => {
  if (!Array.isArray(rawArr) || !rawArr.length) return { bases: [], createdAt: null };

  const filtered = vendedorSelecionado !== null
    ? rawArr.filter(v => v?.id === vendedorSelecionado)
    : rawArr;

  return {
    bases: mergeByAnoMes(
      filtered.flatMap(v => v?.baseSuper4 ?? []),
      ['positivacaoCimento', 'positivacaoColante'],
      ['percentMediaDp', 'mediaJsm'],
    ),
    createdAt: filtered.find(v => v?.createdAt)?.createdAt ?? null,
  };
};

const extractItems = (source) => {
  if (Array.isArray(source?.[0]?.[0]?.data)) {
    return source[0][0]?.data?.[0] ?? [];
  }

  const arr =
    source?.data?.data?.[0] ??
    source?.data?.[0] ??
    source?.[0] ??
    null;

  return Array.isArray(arr) ? arr : [];
};

const splitByYtd = (arr) => ({
  ytd:    arr.find(p => p.ytd) ?? {},
  mensal: arr.filter(p => !p.ytd),
});

const pct = (val, dec = 1) => parseFloat(((val ?? 0) * 100).toFixed(dec));

const formatarDataHora = (raw) => {
  if (!raw) return { data: "--", hora: "--" };
  const d = new Date(raw);
  return {
    data: d.toLocaleDateString("pt-BR"),
    hora: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  };
};

const computeSuper4 = ({ bases, createdAt }) => {
  const { ytd, mensal } = splitByYtd(bases);

  const { data: dataAtualizacao, hora: horaAtualizacao } = formatarDataHora(createdAt);

  const labels = ["YTD 26", ...mensal.map(p => formatMes(p.anoMes))];

  const dadosCimentos = {
    data:   [Math.round(ytd.positivacaoCimento ?? 0), ...mensal.map(p => p.positivacaoCimento)],
    colors: ["#3D7A3D", "#2ECC2E", "#52BF52", "#28B228", "#47BF47"],
  };
  const dadosPremium = {
    data:   [pct(ytd.percentMediaDp), ...mensal.map(p => pct(p.percentMediaDp))],
    colors: ["#CC6000", "#E88A00", "#F5C800", "#F0C200", "#F5CC00"],
  };
  const dadosColantes = {
    data:   [Math.round(ytd.positivacaoColante ?? 0), ...mensal.map(p => p.positivacaoColante)],
    colors: ["#0A2473", "#4299E1", "#90CDF4", "#3182CE", "#63B3ED"],
  };
  const dadosJs = {
    data:   [pct(ytd.mediaJsm, 2), ...mensal.map(p => pct(p.mediaJsm, 2))],
    colors: ["#7A1515", "#CC2020", "#E03030", "#D04040", "#CC2828"],
  };

  return { labels, dadosCimentos, dadosPremium, dadosColantes, dadosJs, dataAtualizacao, horaAtualizacao };
};



export const useSuper4Data = () => {
  const { vendedorSelecionado } = useVendedor();
  const [items, setItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: super4Data, isFetching, isRefetching } =
    useGetSuper4(!hasMock && navigator.onLine, 0, 0, null);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.super4.toArray();
      if (rows?.length) setItems(rows[0]?.payload ?? null);
    } catch (err) {
      console.error('Erro ao carregar super4 do Dexie:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    if (hasMock) {
      setItems(extractItems(super4Mock));
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (hasMock) return;
    window.addEventListener('online', loadFromDexie);
    return () => window.removeEventListener('online', loadFromDexie);
  }, [loadFromDexie]);


  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    if (!super4Data) return;

    const rawItems = extractItems(super4Data);
    setItems(rawItems);

    db.super4.clear()
      .then(() => db.super4.add({ payload: rawItems, syncedAt: new Date().toISOString() }))
      .catch(err => console.error('Erro ao sincronizar super4 no IndexedDB:', err));

    setIsRefreshing(false);
    setIsLoading(false);
  }, [isFetching, isRefetching, super4Data]);

  const normalized = useMemo(
    () => flattenVendedorSuper4(items ?? [], vendedorSelecionado),
    [items, vendedorSelecionado]
  );

  const computed = useMemo(() => computeSuper4(normalized), [normalized]);

  return { ...computed, isLoading, isRefreshing };
};
